import React from 'react';

/**
 * One datapoint of a version-aware time series.
 *
 * `versions` identifies how the value was computed (e.g. scoring heuristic
 * version, embedding model version). Points whose version tuple differs from
 * the latest point's tuple are rendered dashed/faded — the series stays
 * visible across version bumps without pretending the values are directly
 * comparable. This is the shared convention for Forge metric trends
 * (cleanliness today; PR Inference OKRs as it gains heuristic versions).
 */
export interface VersionedTrendPoint {
  timestamp: string;
  value: number | null;
  versions: Record<string, string | null | undefined>;
  /** Extra tooltip lines shown under the timestamp/value line. */
  detail?: string[];
}

interface VersionedTrendChartProps {
  points: VersionedTrendPoint[];
  ariaLabel: string;
  height?: number;
  width?: number;
  /** Fixed value domain; defaults to padded min/max of the data. */
  domain?: [number, number];
  color?: string;
  formatValue?: (value: number) => string;
  formatTimestamp?: (iso: string) => string;
}

function versionKey(point: VersionedTrendPoint): string {
  return JSON.stringify(
    Object.keys(point.versions)
      .sort()
      .map((key) => [key, point.versions[key] ?? null])
  );
}

function versionLabel(point: VersionedTrendPoint): string {
  return Object.entries(point.versions)
    .map(([key, value]) => `${key} ${value ?? '?'}`)
    .join(' · ');
}

const PADDING = { top: 10, right: 12, bottom: 20, left: 34 };

export const VersionedTrendChart: React.FC<VersionedTrendChartProps> = ({
  points,
  ariaLabel,
  height = 120,
  width = 340,
  domain,
  color = 'var(--accent)',
  formatValue = (value) => value.toFixed(1),
  formatTimestamp = (iso) => new Date(iso).toLocaleDateString(),
}) => {
  const values = points
    .map((point) => point.value)
    .filter((value): value is number => value !== null);
  if (points.length === 0 || values.length === 0) return null;

  const [minValue, maxValue] = domain ?? [
    Math.floor(Math.min(...values) - 2),
    Math.ceil(Math.max(...values) + 2),
  ];
  const valueRange = Math.max(1e-6, maxValue - minValue);
  const innerWidth = width - PADDING.left - PADDING.right;
  const innerHeight = height - PADDING.top - PADDING.bottom;
  const xAt = (index: number) =>
    PADDING.left +
    (points.length <= 1 ? innerWidth / 2 : (index / (points.length - 1)) * innerWidth);
  const yAt = (value: number) =>
    PADDING.top + (1 - Math.max(0, Math.min(1, (value - minValue) / valueRange))) * innerHeight;

  const currentKey = versionKey(points[points.length - 1]);
  const isCurrent = (point: VersionedTrendPoint) => versionKey(point) === currentKey;

  const segments: Array<{ x1: number; y1: number; x2: number; y2: number; current: boolean }> = [];
  const boundaries: Array<{ x: number; label: string; pointIndex: number }> = [];
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const point = points[index];
    if (versionKey(previous) !== versionKey(point)) {
      boundaries.push({
        x: (xAt(index - 1) + xAt(index)) / 2,
        label: versionLabel(point),
        pointIndex: index,
      });
    }
    if (previous.value === null || point.value === null) continue;
    segments.push({
      x1: xAt(index - 1),
      y1: yAt(previous.value),
      x2: xAt(index),
      y2: yAt(point.value),
      current: isCurrent(previous) && isCurrent(point),
    });
  }

  return (
    <div style={{ display: 'grid', gap: 5 }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', maxWidth: width * 1.6, height: 'auto' }}
        role="img"
        aria-label={ariaLabel}
      >
        {[0, 0.5, 1].map((ratio) => {
          const value = minValue + valueRange * ratio;
          const y = yAt(value);
          return (
            <g key={ratio}>
              <line
                x1={PADDING.left}
                x2={width - PADDING.right}
                y1={y}
                y2={y}
                stroke="var(--border)"
                strokeWidth={1}
              />
              <text
                x={PADDING.left - 6}
                y={y + 3}
                textAnchor="end"
                fill="var(--text-tertiary)"
                fontSize={8.5}
              >
                {formatValue(value)}
              </text>
            </g>
          );
        })}
        {boundaries.map((boundary, index) => (
          <g key={`${boundary.pointIndex}:${boundary.label}`}>
            <line
              x1={boundary.x}
              x2={boundary.x}
              y1={PADDING.top}
              y2={height - PADDING.bottom}
              stroke="var(--text-tertiary)"
              strokeWidth={1}
              strokeDasharray="2 3"
              opacity={0.7}
            />
            <circle
              cx={boundary.x}
              cy={PADDING.top + 7}
              r={6.5}
              fill="var(--surface)"
              stroke="var(--text-tertiary)"
              strokeWidth={1}
            />
            <text
              x={boundary.x}
              y={PADDING.top + 9.5}
              textAnchor="middle"
              fill="var(--text-secondary)"
              fontSize={7.5}
              fontWeight={800}
            >
              {index + 1}
            </text>
            <title>{`Change ${index + 1}: ${boundary.label}`}</title>
          </g>
        ))}
        {segments.map((segment, index) => (
          <line
            key={index}
            x1={segment.x1}
            y1={segment.y1}
            x2={segment.x2}
            y2={segment.y2}
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeDasharray={segment.current ? undefined : '4 3'}
            opacity={segment.current ? 1 : 0.55}
          />
        ))}
        {points.map((point, index) => {
          if (point.value === null) return null;
          const current = isCurrent(point);
          return (
            <circle
              key={point.timestamp + index}
              cx={xAt(index)}
              cy={yAt(point.value)}
              r={3}
              fill={current ? color : 'var(--surface)'}
              stroke={color}
              strokeWidth={1.5}
              opacity={current ? 1 : 0.6}
              data-testid={`trend-point-${index}`}
              data-current-version={current}
            >
              <title>
                {[
                  `${formatTimestamp(point.timestamp)} · ${formatValue(point.value)}`,
                  versionLabel(point),
                  ...(point.detail ?? []),
                ].join('\n')}
              </title>
            </circle>
          );
        })}
        <text x={PADDING.left} y={height - 6} fill="var(--text-tertiary)" fontSize={8}>
          {formatTimestamp(points[0].timestamp)}
        </text>
        <text
          x={width - PADDING.right}
          y={height - 6}
          textAnchor="end"
          fill="var(--text-tertiary)"
          fontSize={8}
        >
          {formatTimestamp(points[points.length - 1].timestamp)}
        </text>
      </svg>
      {boundaries.length > 0 ? (
        <ol
          aria-label="Scoring and model changes"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px 10px',
            margin: 0,
            padding: 0,
            color: 'var(--text-tertiary)',
            fontSize: 9,
            listStyle: 'none',
          }}
        >
          {boundaries.map((boundary, index) => (
            <li
              key={`${boundary.pointIndex}:${boundary.label}`}
              style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-grid',
                  placeItems: 'center',
                  width: 14,
                  height: 14,
                  border: '1px solid var(--border)',
                  borderRadius: '50%',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 800,
                }}
              >
                {index + 1}
              </span>
              <span>
                {formatTimestamp(points[boundary.pointIndex].timestamp)} · {boundary.label}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
};
